import React from 'react'
import { Box, Heading } from 'react-bulma-components'
import { Translate } from '../../utils/translations'
import EmailConfirmationForm from './email-confirmation-form'
import OwnerCheckForm from './owner-check-form'
import OwnerChangedForm from './owner-changed-form'
import RepairPresentation from '../repair/repair-presentation'
import RepairKitRequestForm from '../repair/repair-kit-request-form'
import Sd500RepairKitRequestStep from '../repair/sd500-repair-kit-request-step'
import RepairKitConfirmationStep from '../repair/repair-kit-confirmation-step'
import { RepairKitRequestFormValues } from '../repair/repair-kit-form-shared'
import { UseWizardHistoryReturn } from '../../hooks/use-wizard-history'
import { WizardStep } from '../../pages/form'
import { getLocale } from 'react-i18nify'
import { campaign } from '../../campaigns'

/**
 * Props for the StepRenderer component
 */
interface StepRendererProps {
  currentStep: string
  onStepSuccess: (stepName: string, data?: any) => void
  onStepAction: (action: string, data?: any) => void
  getStepData: UseWizardHistoryReturn<Record<WizardStep, WizardStep>>['getStepData']
  getStepFormValues: UseWizardHistoryReturn<Record<WizardStep, WizardStep>>['getStepFormValues']
  setStepFormValues: UseWizardHistoryReturn<Record<WizardStep, WizardStep>>['setStepFormValues']
  submissionId?: string
}

/**
 * Component that renders the appropriate form/component based on the current step
 * Centralizes all step-specific rendering logic for better maintainability
 */
const StepRenderer: React.FC<StepRendererProps> = ({
  currentStep,
  onStepSuccess,
  onStepAction,
  getStepData,
  getStepFormValues,
  setStepFormValues,
  submissionId,
}) => {
  const language = getLocale() || 'en'
  // Helper function to get step title
  const getStepTitle = () => {
    const titleMap: Record<string, string> = {
      emailConfirmation: 'emailConfirmation.title',
      ownerCheck: 'ownerCheck.title',
      ownerChanged: 'ownerChanged.title',
      userIsOwner: 'userIsOwner.title',
      crankQuantity: 'recall.title',
      concerned: 'repair.title',
      repairKitRequest: 'repairRequest.title',
      repairKitConfirmation: 'repair.infoConfirmation',
      repairKitRequestSuccess: 'recall.confirmation',
      notConcerned: 'recall.notConcerned',
      unknownNewOwner: 'ownerChanged.confirmation',
    }

    return titleMap[currentStep] || ''
  }

  const requestor = getStepData('emailConfirmation')?.requestor

  // Campaign-specific quantity step component
  const QuantityForm = campaign.QuantityForm!

  // Helper function to render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 'emailConfirmation':
        return (
          <EmailConfirmationForm
            onSuccess={data => onStepSuccess('ownerCheck', data)}
            language={language}
            savedValues={getStepFormValues('emailConfirmation')}
            onSaveValues={values => setStepFormValues('emailConfirmation', values)}
          />
        )

      case 'ownerCheck':
        return (
          <OwnerCheckForm
            onYes={() => onStepAction('goToCrankQuantity', { knownOwner: true })}
            onNo={() => onStepAction('goToOwnerChanged', { knownOwner: false })}
          />
        )

      case 'ownerChanged':
        return (
          <OwnerChangedForm
            onSuccess={data => onStepAction('goToUnknownNewOwner', data)}
            onUnknownNewOwner={() => onStepAction('goToUnknownNewOwner')}
            requestor={requestor}
            language={language}
            submissionId={submissionId}
            savedValues={getStepFormValues('ownerChanged')}
            onSaveValues={values => setStepFormValues('ownerChanged', values)}
          />
        )

      case 'crankQuantity':
        return (
          <QuantityForm
            onSuccess={data => {
              if (campaign.isConcerned!(data.quantity)) {
                onStepAction('goToConcerned', data)
              } else {
                onStepAction('goToNotConcerned', data)
              }
            }}
            requestor={requestor}
            language={language}
            submissionId={submissionId}
            savedValues={getStepFormValues('crankQuantity')}
            onSaveValues={values => setStepFormValues('crankQuantity', values)}
          />
        )

      case 'concerned':
        return (
          <RepairPresentation
            onRequestKit={() => onStepAction('goToRepairKitRequest')}
            quantity={getStepData('crankQuantity')?.quantity}
            repair={campaign.repair!}
          />
        )

      case 'repairKitRequest':
        if (campaign.kitRequestDraftFlow) {
          return (
            <Sd500RepairKitRequestStep
              initialEmail={requestor?.email}
              initialCountry={requestor?.country}
              language={language}
              submissionId={submissionId}
              quantity={getStepData('crankQuantity')?.quantity}
              savedValues={getStepFormValues('repairKitRequest')}
              onSaveValues={values => setStepFormValues('repairKitRequest', values)}
              onDraftSaved={(kitRequestId, values) => {
                setStepFormValues('repairKitRequest', values)
                onStepAction('goToRepairKitConfirmation', { kitRequestId })
              }}
            />
          )
        }
        return (
          <RepairKitRequestForm
            initialEmail={requestor?.email}
            initialCountry={requestor?.country}
            onSuccess={(values: RepairKitRequestFormValues) => {
              setStepFormValues('repairKitRequest', values)
              onStepAction('goToRepairKitConfirmation')
            }}
            language={language}
            savedValues={getStepFormValues('repairKitRequest')}
            onSaveValues={values => setStepFormValues('repairKitRequest', values)}
          />
        )

      case 'repairKitConfirmation':
        return (
          <RepairKitConfirmationStep
            formValues={getStepFormValues('repairKitRequest') as RepairKitRequestFormValues | undefined}
            quantity={getStepData('crankQuantity')?.quantity}
            language={language}
            submissionId={submissionId}
            kitRequestDraftFlow={campaign.kitRequestDraftFlow}
            kitRequestId={getStepData('repairKitRequest')?.kitRequestId}
            onBack={() => onStepAction('goToRepairKitRequest')}
            onSuccess={() => onStepAction('goToRepairKitRequestSuccess')}
          />
        )

      default:
        return null
    }
  }

  return (
    // `data-step` exposes the current step name to E2E tests in an
    // i18n-independent way; plain DOM wrappers guarantee the data-testid
    // attributes reach the DOM regardless of react-bulma prop forwarding.
    <div data-testid="wizard-step" data-step={currentStep}>
      <Box>
        <Heading size={4} textAlign="center">
          <span data-testid="step-title">
            {getStepTitle() && <Translate value={getStepTitle()} />}
          </span>
        </Heading>

        <div data-testid="step-content">{renderStepContent()}</div>
      </Box>
    </div>
  )
}

export default StepRenderer
